import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { array, boolean, mixed, number, object, string } from 'yup';
import { emptyHypotheses, initialState, uid } from './data';
import { calculate } from './mocks';
import { lengthKm, totalDemand } from './domain';
import type { CalculationInput, DevelopmentPlan, Energy, Hypotheses, Project, StudioState } from './types';

const KEY = 'plan-unifie-v1';
const position = array().of(number().required()).length(2).required();
const rule = object({ mode: mixed().oneOf(['weighted', 'forbidden', 'forced']).required(), weight: number().positive().required() });
const hypotheses = object({
  categories: object().required().test('rules', 'Règles invalides', value => Object.values(value).every(v => rule.isValidSync(v))),
  local: object().required().test('rules', 'Exceptions invalides', value => Object.values(value).every(v => rule.isValidSync(v))),
  departures: array().of(object({
    id: string().required(), label: string().required(), roadId: string().required(),
    kind: mixed().oneOf(['network', 'plant']).required(), coordinates: array().of(position).min(2).required(),
  })).required(),
});
const numericMap = object().required().test('numbers', 'Valeurs numériques invalides',
  value => Object.values(value).every(v => typeof v === 'number' && Number.isFinite(v) && v > 0));
const stateSchema = object({
  project: object({
    name: string().required(), perimeter: string().required(), extent: string().required(), station: string().required(),
    dju: number().positive().required(), heatRatio: number().positive().required(), coldRatio: number().positive().required(),
    networkKind: mixed().oneOf(['existing', 'new']).required(), revision: number().required(),
    stationDju: number().positive().required(),
    ratios: object({ heat: numericMap, cold: numericMap }).required(),
  }).required(),
  scenarios: array().of(object({ id: string().required(), name: string().required(), hypotheses: hypotheses.required() })).required(),
  plans: array().of(object({
    id: string().required(), scenarioId: string().required(), name: string().required(), energy: mixed().oneOf(['heat', 'cold']).required(),
    subscribers: object().required().test('dates', 'Dates invalides', value => Object.values(value).every(v => typeof v === 'string')),
    pipes: array().of(object({
      id: string().required(), name: string().required(), manual: boolean().required(),
      coordinates: array().of(position).min(2).required(), month: string().defined(),
    })).required(), snapshot: hypotheses.optional().default(undefined), stale: boolean().required(),
    result: object({
      calculatedAt: string().required(), energy: mixed().oneOf(['heat', 'cold']).optional(), demand: number().required(), lengthKm: number().required(),
      subscribers: number().required(), revision: number().required(),
    }).optional().default(undefined),
  })).required(),
  buildings: array().of(object({
    id: string().required(), name: string().required(), node: number().integer().required(),
    demand: number().required(), usage: string().required(), period: string().required(), surface: number().positive().required(),
    sourceVersion: number().required(), conflict: boolean().required(),
    corrections: object().required().test('corrections', 'Corrections invalides', value =>
      Object.entries(value).every(([key, v]) => ['heat', 'cold'].includes(key) && typeof v === 'number' && Number.isFinite(v) && v > 0)),
  })).required(),
  synced: boolean().required(),
});

function load(): { state: StudioState; error: string } {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { state: initialState(), error: '' };
    const candidate: unknown = JSON.parse(raw);
    if (!stateSchema.isValidSync(candidate, { strict: true })) throw new Error('Format de sauvegarde invalide');
    // Schema validation above checks every field consumed by the prototype.
    return { state: candidate as StudioState, error: '' };
  } catch (error) {
    return { state: initialState(), error: `Sauvegarde locale illisible : ${error instanceof Error ? error.message : String(error)}. Réinitialisez explicitement pour enregistrer à nouveau.` };
  }
}
export const sameHypotheses = (a: Hypotheses, b: Hypotheses) => JSON.stringify(a) === JSON.stringify(b);

export function useStudio() {
  const [loaded] = useState(load);
  const [state, setState] = useState(loaded.state);
  const [storageError, setStorageError] = useState(loaded.error);
  useEffect(() => {
    if (storageError) return;
    try { localStorage.setItem(KEY, JSON.stringify(state)); }
    catch (error) { setStorageError(`Enregistrement local impossible : ${error instanceof Error ? error.message : String(error)}`); }
  }, [state, storageError]);

  const updatePlan = (id: string, patch: Partial<DevelopmentPlan>) => {
    setState(s => ({ ...s, plans: s.plans.map(p => p.id === id ? { ...p, ...patch } : p) }));
  };
  const updateHypotheses = (id: string, h: Hypotheses) => {
    setState(s => ({ ...s, scenarios: s.scenarios.map(c => c.id === id ? { ...c, hypotheses: h } : c) }));
  };
  const saveProject = (project: Project) => setState(s => {
    const invalidate = project.station !== s.project.station || project.dju !== s.project.dju ||
      project.stationDju !== s.project.stationDju || JSON.stringify(project.ratios) !== JSON.stringify(s.project.ratios);
    return {
      ...s, project: { ...project, revision: s.project.revision + (invalidate ? 1 : 0) },
      plans: s.plans.map(p => ({ ...p, stale: p.stale || ((!!p.snapshot || p.pipes.length > 0) && invalidate) })),
    };
  });
  const addScenario = () => {
    const id = uid();
    setState(s => ({ ...s, scenarios: [...s.scenarios, {
      id, name: `Scénario ${s.scenarios.length + 1}`, hypotheses: emptyHypotheses(),
    }] }));
    return id;
  };
  const addPlan = (scenarioId: string) => {
    const id = uid();
    setState(s => ({ ...s, plans: [...s.plans, {
      id, scenarioId, name: `Plan de développement ${s.plans.filter(p => p.scenarioId === scenarioId).length + 1}`,
      energy: 'heat', subscribers: {}, pipes: [], stale: false,
    }] }));
    return id;
  };
  const mutation = useMutation({
    mutationFn: calculate,
    onSuccess: (pipes, input: CalculationInput) => {
      setState(s => ({ ...s, plans: s.plans.map(p => p.id === input.plan.id ? {
        ...p, pipes, snapshot: structuredClone(input.hypotheses), stale: false, projectRevision: s.project.revision,
        result: {
          calculatedAt: new Date().toISOString(), energy: input.plan.energy, demand: totalDemand(input.plan, s.buildings, s.project),
          lengthKm: lengthKm({ pipes }), subscribers: Object.keys(input.plan.subscribers).length, revision: s.project.revision,
        },
      } : p) }));
    },
  });
  const reset = () => {
    try { localStorage.removeItem(KEY); setStorageError(''); setState(initialState()); }
    catch (error) { setStorageError(`Réinitialisation impossible : ${String(error)}`); }
  };
  const correct = (id: string, energy: Energy, value: number | undefined) => setState(s => ({
    ...s, project: { ...s.project, revision: s.project.revision + 1 },
    buildings: s.buildings.map(b => {
      if (b.id !== id) return b;
      const corrections = { ...b.corrections };
      if (value === undefined) delete corrections[energy]; else corrections[energy] = value;
      return { ...b, corrections };
    }),
    // Ciblage conservatoire de démonstration, explicitement à arbitrer dans l'interface.
    plans: s.plans.map(p => ({ ...p, stale: p.stale || (!!p.snapshot && id in p.subscribers) })),
  }));
  const synchronize = () => setState(s => ({
    ...s, synced: true, project: { ...s.project, revision: s.project.revision + 1 },
    buildings: s.buildings.map(b => b.id === 'c1' ? { ...b, surface: Math.round(b.surface * 1.1), sourceVersion: 2 } :
      ['c2', 'c6'].includes(b.id) && s.plans.some(p => b.id in p.subscribers) ? { ...b, conflict: true } : b),
    plans: s.plans.map(p => ({ ...p, stale: p.stale || (!!p.result &&
      ('c1' in p.subscribers || 'c2' in p.subscribers || 'c6' in p.subscribers)) })),
  }));
  return { state, setState, updatePlan, updateHypotheses, saveProject, addScenario, addPlan, mutation, storageError, reset, correct, synchronize };
}
export type Studio = ReturnType<typeof useStudio>;
